import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AddCircleOutline,
  AddOutlined,
  SavingsOutlined,
  SyncOutlined,
} from "@mui/icons-material";
import { Box, Button, Divider, IconButton, Tooltip } from "@mui/material";
import isNil from "lodash/isNil";
import size from "lodash/size";

import PrimaryContentCard from "../../../../_src_shared/components/layout/PrimaryContentCard";
import EmptyStatePlaceholder from "../../../../components/feedback/EmptyStatePlaceholder";
import Flx from "../../../../components/layout/Flx";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import { useLiquidityHook } from "../../../../hooks/useLiquidityHook";
import {
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../../../../redux/actions/sidebarActions";
import {
  loanFetchedDataLiquiditySourcesSet,
  loanFetchedDataRequiredLiquiditySet,
} from "../../../../redux/reducers/loanFetchedDataReducer";
import { sourceRequiresAssociations } from "./constants";
import LiquiditySourcesTable from "./LiquiditySourcesTable";
import LiquiditySummaryColumn from "./LiquiditySummaryColumn";
import RequiredLiquidityCard from "./RequiredLiquidityCard";
import LiquidityCardSectionHeader from "./LiquidityCardSectionHeader";
import LoanDocumentManagerTable from "../LoanDrilldownDocumentsTab/LoanDocumentManagerTable";
import {
  CARD_FILES_GRID_SX,
  CARD_TABLE_BOX_SX,
} from "../../shared/cardGrid";

const LiquidityFilesSection = () => {
  const loanDocuments = useSelector(
    (state) => state.loanDrilldown?.loanDocuments,
  );

  const liquidityFiles = useMemo(
    () => (loanDocuments || []).filter((d) => d?.docGroup === "Liquidity"),
    [loanDocuments],
  );

  return (
    <Box>
      <LiquidityCardSectionHeader
        title={
          isNil(loanDocuments)
            ? "Liquidity files"
            : `Liquidity files (${size(liquidityFiles)})`
        }
        subTitle={`Loan documents in the "Liquidity" doc group.`}
      />
      {size(liquidityFiles) === 0 ? (
        <EmptyStatePlaceholder
          icon={<SavingsOutlined />}
          title="No liquidity files yet"
          description='Upload a document to the "Liquidity" doc group from the Documents tab to see it here.'
        />
      ) : (
        // Same embedding as the DSCR tab's Supporting files: the table draws
        // `noBorder` itself, so the box supplies the edge without doubling it.
        <Box sx={CARD_TABLE_BOX_SX}>
          <LoanDocumentManagerTable
            rowData={liquidityFiles}
            hideLayouts
            // Size to the rows — the 640px floor is for the full Documents tab
            // and leaves a slab of empty grid here.
            minHeight={0}
            toolbarPadding={1.25}
            gridSx={CARD_FILES_GRID_SX}
          />
        </Box>
      )}
    </Box>
  );
};

const LoanDrilldownLiquidityCalculationsTab = () => {
  const dispatch = useDispatch();
  const loan = useSelector((state) => state.loanDrilldown);
  const liquiditySources = useSelector(
    (state) => state.loanFetchedData?.liquiditySources,
  );
  const requiredLiquidity = useSelector(
    (state) => state.loanFetchedData?.requiredLiquidity,
  );
  const {
    loading,
    getLiquiditySources,
    getRequiredLiquidity,
    removeLiquiditySourceAssociation,
  } = useLiquidityHook();

  // Active row in the table = the source currently being edited in the
  // sidebar. Drives the highlight in LiquiditySourcesTable.
  const sidebarType = useSelector((state) => state.sidebar?.type);
  const sidebarSourceState = useSelector((state) => state.sidebar?.state);
  const activeSourceId =
    sidebarType === loanDrilldownSidebarTypes.ADD_EDIT_LIQUIDITY_SOURCE
      ? sidebarSourceState?._id || null
      : null;

  useEffect(() => {
    if (isNil(liquiditySources) && loan?._id) {
      getLiquiditySources({
        dealId: loan._id,
        onSuccessFn: (data) =>
          dispatch(loanFetchedDataLiquiditySourcesSet(data)),
      });
    }
  }, [liquiditySources, loan?._id]);

  useEffect(() => {
    if (isNil(requiredLiquidity) && loan?._id) {
      getRequiredLiquidity({
        dealId: loan._id,
        onSuccessFn: (data) =>
          dispatch(loanFetchedDataRequiredLiquiditySet(data)),
      });
    }
  }, [requiredLiquidity, loan?._id]);

  const [localRequired, setLocalRequired] = useState(null);
  useEffect(() => {
    if (!isNil(requiredLiquidity)) setLocalRequired(requiredLiquidity);
  }, [requiredLiquidity]);

  const onAdd = useCallback(() => {
    dispatch(
      sidebarTypeSet(loanDrilldownSidebarTypes.ADD_EDIT_LIQUIDITY_SOURCE),
    );
    dispatch(sidebarSetStateValue(null));
  }, [dispatch]);

  const onRowClick = useCallback(
    (source) => {
      dispatch(
        sidebarTypeSet(loanDrilldownSidebarTypes.ADD_EDIT_LIQUIDITY_SOURCE),
      );
      dispatch(sidebarSetStateValue(source));
    },
    [dispatch],
  );

  // Pending chip-remove requests, keyed by `sourceId:type:id`. Drives the
  // per-chip loading spinner so the operator sees instant feedback when they
  // click ×.
  const [pendingRemovals, setPendingRemovals] = useState(() => new Set());

  // Pull a single id out of one of a source's reference arrays — fired by the
  // × on each chip in the table. Server returns { source, sources }; we
  // dispatch the .sources slice so the table re-renders without merging.
  // Verified-liquidity rollups (per-borrower + loan-level) update as a side
  // effect on the same request.
  const onRemoveAssociation = useCallback(
    (source, associationType, associationId) => {
      if (!source?._id || !loan?._id || !associationType || !associationId) {
        return;
      }
      const key = `${source._id}:${associationType}:${associationId}`;
      setPendingRemovals((prev) => {
        if (prev.has(key)) return prev;
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      const clearPending = () =>
        setPendingRemovals((prev) => {
          if (!prev.has(key)) return prev;
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      removeLiquiditySourceAssociation({
        dealId: loan._id,
        sourceId: source._id,
        associationType,
        associationId,
        onSuccessFn: (data) =>
          dispatch(loanFetchedDataLiquiditySourcesSet(data?.sources)),
        onCompleteFn: clearPending,
        onFailFn: clearPending,
      });
    },
    [loan?._id, removeLiquiditySourceAssociation, dispatch],
  );

  const totals = useMemo(
    () => computeTotals(liquiditySources),
    [liquiditySources],
  );
  const requiredTotal = useMemo(
    () => computeRequiredTotal(localRequired ?? requiredLiquidity),
    [localRequired, requiredLiquidity],
  );
  const coverage = useMemo(() => {
    if (!requiredTotal || requiredTotal === 0) return null;
    return totals.validated / requiredTotal;
  }, [totals.validated, requiredTotal]);

  const isInitialLoading =
    (isNil(liquiditySources) || isNil(requiredLiquidity)) && loading;

  return (
    <Box sx={{ py: 4, px: 3 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(0, 2fr) minmax(0, 1fr)",
          },
          gap: 3,
          maxWidth: "1600px",
          mx: "auto",
        }}
      >
        <Flx column gap={3} sx={{ minWidth: 0 }}>
          <PrimaryContentCard
            loading={isInitialLoading}
            titleEndContent={<RefreshButton />}
            subTitle="Track the borrower's available liquidity sources and the minimum required liquidity for this loan."
            title="Liquidity calculations"
            // endContent={
            //   <Button
            //     size="medium"
            //     startIcon={<AddCircleOutline />}
            //     onClick={onAdd}
            //   >
            //     Add Liquidity Source
            //   </Button>
            // }
          >
            <Divider sx={{ mt: 0.5, mb: 5, borderWidth: "1.5px" }} />
            <Flx column g={0} sx={{ px: 1 }}>
              <Box>
                {size(liquiditySources) === 0 ? (
                  <EmptyStatePlaceholder
                    icon={<SavingsOutlined />}
                    title="No liquidity sources yet"
                    description="Add the first cash, brokerage, life insurance, line of credit, or cash-out-proceeds entry for this loan."
                    actions={
                      <Button
                        size="medium"
                        startIcon={<AddCircleOutline />}
                        onClick={onAdd}
                      >
                        Add Liquidity Source
                      </Button>
                    }
                  />
                ) : (
                  <>
                    <LiquidityCardSectionHeader
                      title={
                        isNil(liquiditySources)
                          ? "Available liquidity"
                          : `Available liquidity (${size(liquiditySources)})`
                      }
                      subTitle="Cash out proceeds do not require a source document or associated party."
                      endContent={
                        <Button
                          size="small"
                          startIcon={<AddOutlined sx={{ fontSize: 14 }} />}
                          onClick={onAdd}
                        >
                          Add Source
                          {/* Add Liquidity Source */}
                        </Button>
                      }
                    />
                    <LiquiditySourcesTable
                      sources={liquiditySources || []}
                      loan={loan}
                      onRowClick={onRowClick}
                      onRemoveAssociation={onRemoveAssociation}
                      pendingRemovals={pendingRemovals}
                      activeSourceId={activeSourceId}
                    />
                  </>
                )}
              </Box>

              {/* <Divider /> */}
              <Divider sx={{ my: 6, mx: -2 }} />
              <RequiredLiquidityCard
                local={localRequired}
                setLocal={setLocalRequired}
              />
              <Divider sx={{ my: 6, mx: -2 }} />

              <LiquidityFilesSection />
            </Flx>
          </PrimaryContentCard>
        </Flx>

        <Box
          sx={{
            position: { xs: "static", md: "sticky" },
            top: 24,
            alignSelf: "flex-start",
            minWidth: 0,
          }}
        >
          <LiquiditySummaryColumn
            totals={totals}
            requiredTotal={requiredTotal}
            coverage={coverage}
            sources={liquiditySources || []}
            totalVerifiedLiquidity={loan?.totalVerifiedLiquidity}
          />
        </Box>
      </Box>
    </Box>
  );
};

/* ────────────── helpers ────────────── */

const computeTotals = (sources) => {
  const empty = {
    gross: 0,
    validated: 0,
    verified: 0,
    byTypeGross: {},
    byTypeValidated: {},
    count: 0,
    countVerified: 0,
    countRequiringDoc: 0,
    countMissingDoc: 0,
  };
  if (!sources || sources.length === 0) return empty;

  const byTypeGross = {};
  const byTypeValidated = {};
  let gross = 0;
  let validated = 0;
  let verified = 0;
  let countVerified = 0;
  let countMissingDoc = 0;
  let countRequiringDoc = 0;

  sources.forEach((s) => {
    const amount = Number(s.amount) || 0;
    const pct =
      s.countedPercentage === null || s.countedPercentage === undefined
        ? 100
        : Number(s.countedPercentage);
    // Prefer server-computed validatedAmount; fall back to local calc.
    const counted =
      s.validatedAmount === null || s.validatedAmount === undefined
        ? amount * (pct / 100)
        : Number(s.validatedAmount);
    gross += amount;
    validated += counted;
    if (s.verified) {
      verified += counted;
      countVerified += 1;
    }
    byTypeGross[s.sourceType] = (byTypeGross[s.sourceType] || 0) + amount;
    byTypeValidated[s.sourceType] =
      (byTypeValidated[s.sourceType] || 0) + counted;
    if (sourceRequiresAssociations(s.sourceType)) {
      countRequiringDoc += 1;
      const docIds = Array.isArray(s.loanDocumentIds)
        ? s.loanDocumentIds
        : s.loanDocumentId
          ? [s.loanDocumentId]
          : [];
      if (docIds.length === 0) countMissingDoc += 1;
    }
  });

  return {
    gross,
    validated,
    verified,
    byTypeGross,
    byTypeValidated,
    count: sources.length,
    countVerified,
    countRequiringDoc,
    countMissingDoc,
  };
};

const computeRequiredTotal = (required) => {
  if (!required) return 0;
  const months = Number(required.monthsOfPaymentsRequired) || 0;
  const payment = Number(required.monthlyPaymentAmount) || 0;
  const closing = Number(required.closingCostsNotFromProceeds) || 0;
  const down = Number(required.downPayment) || 0;
  return months * payment + closing + down;
};

const RefreshButton = () => {
  const dispatch = useDispatch();
  const liquiditySources = useSelector(
    (state) => state.loanFetchedData?.liquiditySources,
  );
  const disabled = isNil(liquiditySources);
  return (
    <Tooltip title="Refresh liquidity data" placement="top">
      <span>
        <IconButton
          size="small"
          onClick={() => dispatch(loanFetchedDataLiquiditySourcesSet(null))}
          disabled={disabled}
        >
          <SyncOutlined />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default LoanDrilldownLiquidityCalculationsTab;
